import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useState } from 'react';

import { NumberInput } from '~/components/inputs/NumberInput';
import type { Candidate } from '~/types/candidate';
import type { MillageInputsForm } from '~/types/form-types';
import type { Configuration } from '~/types/project';
import { formatCurrency } from '~/utils/formatting-utils';

interface CandidateHeaderProps {
  candidate: Candidate;
  /** Current configuration (for bond series information) */
  configuration: Configuration;
  /** Current configuration's millage inputs (for editing) */
  millageInputs: MillageInputsForm;
  /** Callback when Run Configuration is clicked with updated millage inputs */
  onRunConfiguration?: (updatedMillageInputs: MillageInputsForm) => void;
}

export const CandidateHeader: React.FC<CandidateHeaderProps> = ({
  candidate,
  configuration: _configuration,
  millageInputs,
  onRunConfiguration,
}) => {
  const [localMillageInputs, setLocalMillageInputs] = useState<MillageInputsForm>(millageInputs);

  // Update local state when millageInputs prop changes (e.g., configuration switch)
  useEffect(() => {
    setLocalMillageInputs(millageInputs);
  }, [millageInputs]);

  const handleMillageChange = (field: keyof MillageInputsForm, value: string) => {
    setLocalMillageInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRunConfiguration = () => {
    if (onRunConfiguration) {
      onRunConfiguration(localMillageInputs);
    }
  };

  return (
    <Box p={4} borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white" mb={4}>
      <HStack justify="space-between" align="flex-end" width="100%" gap={4}>
        {/* LEFT GROUP: Revenue, Principal, Efficiency */}
        <HStack gap={6}>
          {/* Revenue */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center">
              Revenue
            </Text>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              {formatCurrency(candidate.results.totalRevenue, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Text>
          </VStack>

          {/* Principal */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center">
              Principal
            </Text>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              {formatCurrency(candidate.results.totalPrincipal, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Text>
          </VStack>

          {/* Efficiency */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center">
              Efficiency
            </Text>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              {(candidate.results.efficiency * 100).toFixed(1)}%
            </Text>
          </VStack>
        </HStack>

        {/* RIGHT GROUP: Peak, Duration, Min, Duration, Run */}
        <HStack gap={4} justify="flex-end">
          {/* Peak Millage */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center" mb={1}>
              Peak
            </Text>
            <Box width="80px">
              <NumberInput
                value={localMillageInputs.peakMillage}
                onChange={(value) => handleMillageChange('peakMillage', value)}
                size="sm"
                min={0.01}
                max={20.0}
                step={0.01}
                precision={2}
                hideSteppers={true}
              />
            </Box>
          </VStack>

          {/* Peak Duration */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center" mb={1}>
              Duration
            </Text>
            <Box width="70px">
              <NumberInput
                value={localMillageInputs.peakMillageDuration}
                onChange={(value) => handleMillageChange('peakMillageDuration', value)}
                size="sm"
                min={1}
                max={19}
                step={1}
                formatOptions={{ useGrouping: false, maximumFractionDigits: 0 }}
                hideSteppers={true}
              />
            </Box>
          </VStack>

          {/* Minimum Millage */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center" mb={1}>
              Min
            </Text>
            <Box width="80px">
              <NumberInput
                value={localMillageInputs.minimumMillage}
                onChange={(value) => handleMillageChange('minimumMillage', value)}
                size="sm"
                min={0.01}
                max={99.99}
                step={0.01}
                precision={2}
                hideSteppers={true}
              />
            </Box>
          </VStack>

          {/* Minimum Duration */}
          <VStack align="center" gap={0}>
            <Text fontSize="xs" color="gray.600" textAlign="center" mb={1}>
              Duration
            </Text>
            <Box width="70px">
              <NumberInput
                value={localMillageInputs.minimumMillageDuration}
                onChange={(value) => handleMillageChange('minimumMillageDuration', value)}
                size="sm"
                min={1}
                max={19}
                step={1}
                formatOptions={{ useGrouping: false, maximumFractionDigits: 0 }}
                hideSteppers={true}
              />
            </Box>
          </VStack>

          {/* Run Button */}
          {onRunConfiguration && (
            <Button size="lg" colorPalette="blue" onClick={handleRunConfiguration}>
              Run
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );
};
